import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "How far is Coral Springs from Boca Raton?",
    answer:
      "Coral Springs is approximately 20 minutes from Boca Raton by car. Boca Raton is in Palm Beach County, just north of Broward County where Coral Springs is located. The short drive makes daily commuting practical for many Boca Raton patients during the five-day SAINT® treatment course.",
  },
  {
    question:
      "Why would a Boca Raton patient consider SAINT® over conventional TMS?",
    answer:
      "SAINT® compresses the treatment course into five consecutive days rather than six weeks of daily visits. It uses fMRI-guided targeting on the Magnus Neuromodulation System. For Boca Raton patients who cannot commit to six weeks or who want an accelerated option, SAINT® may be worth considering. Conventional TMS — broadly covered by insurance — remains an option if SAINT® is not appropriate or not covered.",
  },
  {
    question: "Can I commute from Boca Raton for the five treatment days?",
    answer:
      "Yes. The approximately 20-minute drive makes daily commuting feasible. SAINT® sessions run throughout the day with 50-minute breaks, so you are at the clinic for most of each day. Some patients prefer to stay near the clinic to minimize travel during treatment.",
  },
  {
    question: "Is the consultation available remotely for Boca Raton patients?",
    answer:
      "Yes. The consultation can be done in person at the Coral Springs clinic or remotely. It reviews your diagnosis, medication history, and treatment goals, and includes screening for TMS and MRI safety. The fMRI scan is arranged before treatment begins.",
  },
  {
    question: "Is SAINT® covered by insurance for Boca Raton patients?",
    answer:
      "Coverage varies by plan, not by city. SAINT® uses Category III CPT codes, and many plans do not yet cover them. Neuro Wellness verifies benefits before treatment and provides a written estimate. If SAINT® is not covered, conventional TMS — which is broadly covered — may be an alternative.",
  },
];

export const pageSaintBocaRaton: LearnPageData = {
  slug: "saint-tms-boca-raton",
  title: "SAINT® TMS for Boca Raton Patients | Neuro Wellness",
  description:
    "SAINT® TMS for Boca Raton patients at Neuro Wellness in Coral Springs. Palm Beach County access, the 20-minute drive, five-day accelerated schedule, and evaluation process.",
  canonical: "/locations/saint-tms-boca-raton",
  h1: "SAINT® TMS for Patients from Boca Raton",
  breadcrumbLabel: "SAINT® TMS for Boca Raton Patients",
  crumbs: baseCrumbs("SAINT® TMS for Boca Raton Patients"),
  shortAnswer:
    "Neuro Wellness TMS Centers of America is in Coral Springs, approximately 20 minutes south of Boca Raton. Boca Raton patients access the FDA-cleared SAINT® protocol — ten sessions per day for five days with fMRI-guided targeting — with a short commute. The five-day schedule makes the drive practical compared to the six weeks required by conventional TMS. The evaluation may be done in person or remotely, with the fMRI arranged before treatment begins.",
  keyTakeaways: [
    "Coral Springs is about 20 minutes from Boca Raton.",
    "Boca Raton is in Palm Beach County, just north of Coral Springs.",
    "The five-day schedule makes commuting practical.",
    "SAINT® uses fMRI-guided targeting on the Magnus system.",
    "The evaluation can be in person or remote.",
  ],

  sections: [
    {
      heading: "Palm Beach County Access",
      body: (
        <>
          {P(
            "Boca Raton is in Palm Beach County, just north of Broward County where Coral Springs is located. The drive is approximately 20 minutes, making Neuro Wellness easily accessible for Boca Raton patients seeking the FDA-cleared SAINT® protocol.",
          )}
          {P(
            "While many Palm Beach County clinics offer conventional TMS, SAINT®'s fMRI-guided accelerated protocol is available at a limited number of verified providers. Neuro Wellness offers it in Coral Springs.",
          )}
        </>
      ),
    },
    {
      heading: "Why Someone May Consider Accelerated Treatment",
      body: (
        <>
          {P(
            "Conventional TMS requires six weeks of daily visits — a significant time commitment for working adults and caregivers. SAINT® compresses the course into five consecutive days. For Boca Raton patients who cannot commit to six weeks, or who are in a severe episode and want an accelerated option, SAINT® may be worth considering.",
          )}
          {P(
            "The trade-off is cost: conventional TMS is broadly covered by insurance, while SAINT® is typically self-pay. A consultation helps determine which is appropriate for your situation.",
          )}
        </>
      ),
    },
    {
      heading: "Travel During a 5-Day Treatment Schedule",
      body: (
        <>
          {P(
            "SAINT® is delivered over five consecutive days, with ten sessions per day of about 10 minutes each, separated by roughly 50-minute breaks. You spend most of each day at the clinic. The 20-minute drive from Boca Raton makes daily commuting practical, though some patients prefer to stay near the clinic for convenience.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/what-to-expect-during-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                what happens during the five days
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Private Outpatient Care",
      body: (
        <>
          {P(
            "Neuro Wellness delivers SAINT® in a private outpatient clinic, not a hospital. There is no overnight stay and no anesthesia. Patients are awake throughout treatment. The environment is designed to feel calm and supportive during the five-day course.",
          )}
        </>
      ),
    },
    {
      heading: "Evaluation Process",
      body: (
        <>
          {P(
            "The evaluation reviews your diagnosis, medication history, and treatment goals. It includes screening for TMS and MRI safety — metal implants, implanted devices, seizure history — and screening for bipolar disorder, psychosis, and substance use. Boca Raton patients can do the evaluation in person or remotely.",
          )}
          {P(
            <>
              To request an evaluation,{" "}
              <a
                href="/contactus"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                contact our team
              </a>{" "}
              or call 954-827-9793.
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/locations/saint-tms-boca-raton#webpage`,
        name: "SAINT® TMS for Patients from Boca Raton",
        url: `${SITE_URL}/locations/saint-tms-boca-raton`,
        description:
          "SAINT® TMS for Boca Raton patients at Neuro Wellness in Coral Springs. Palm Beach County access and the five-day accelerated schedule.",
      }),
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/locations/saint-tms-boca-raton#faq`),
      breadcrumbJsonld(baseCrumbs("SAINT® TMS for Boca Raton Patients")),
    ],
  },
  relatedLinks: [
    ...sharedRelated,
    {
      title: "SAINT® TMS in Florida",
      href: "/saint-tms-florida",
      description: "Treatment access for Florida patients",
    },
    {
      title: "How Much Does SAINT® TMS Cost?",
      href: "/learn/saint-tms-cost",
      description: "Cost components and financing",
    },
  ],
};
